import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Chat from '../Chat/chat';
import { apiCall } from '../utils/api';
import Loading from '../components/Loading/Loading';
import LoginV3 from '../Chat/Login/login-v3';

const EXPERT_SLUG_MAP = {
    nishantkumar: ['nishant kumar', 'nishantkumar', 'nishant'],
    scannerexpert: ['scanner expert', 'scannerexpert', 'scanneradda expert', 'scanneradda'],
    neerajarora: ['neeraj arora', 'neerajarora', 'neeraj'],
    aakashkandoi: ['aakash kandoi', 'aakashkandoi', 'aakash'],
    vanshgoyal: ['vansh goyal', 'vanshgoyal', 'vansh'],
    kushallodha: ['kushal lodha', 'kushallodha', 'kushal'],
    umeshsharma: ['umesh sharma', 'umeshsharma', 'umesh'],
    'jksc-audit-expert': ['jksc audit expert', 'jksc', 'jk sc audit', 'jksc audit', 'audit expert'],
    paraggupta: ['parag gupta', 'paraggupta', 'parag'],
    gujaratgovt: ['gujarat government ai expert', 'gujaratgovt', 'gujarat government', 'gujarat ai expert', '69c0cfdef4f772ce6f8ce5b2'],
    chinmaya: ['chinmaya', 'chin maya'],
};

const INTRO_MESSAGES = {
    nishantkumar: 'Hi! I’m CA Nishant Kumar—ask me anything about taxation or CA careers.',
    scannerexpert: 'Scanner Expert here. Share your case, and I’ll scan it fast.',
    neerajarora: 'Hey! I’m Neeraj Arora; let’s talk growth plans and job readiness.',
    aakashkandoi: 'Hi, I am CA Aakash Kandoi. Need help with accounting or incomplete audit reports?',
    vanshgoyal: 'Hello, Vansh Goyal at your service—let’s unlock your income goals.',
    kushallodha: 'Kushal Lodha here. Ask me about finance, investing, or career moves.',
    umeshsharma: "Hi! I'm CA Umesh Sharma—let me help you with your CA queries.",
    'jksc-audit-expert': "Hi! I'm the JKSC Audit Expert - share your toughest audit questions.",
    paraggupta: "Hi, I'm CA Parag Gupta—ask me about audit strategy, client readiness, or career moves.",
    gujaratgovt: "Hello! Gujarat Government AI Expert at your service for state-specific government career guidance.",
    chinmaya: "Hi! I’m Chinmaya. How can I help you today?",
};

const GUJARAT_GOVT_CHARACTER_ID = '69c0cfdef4f772ce6f8ce5b2';

const findCharacterById = (list, id) => {
    if (!Array.isArray(list) || !id) return null;
    const normalizedId = String(id);
    return list.find((item) => {
        const candidateId = resolveCharacterId(item);
        return candidateId && String(candidateId) === normalizedId;
    }) || null;
};

const resolveCharacterId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (Array.isArray(item)) return resolveCharacterId(item[0]);
    if (item.character) {
        const nestedId = resolveCharacterId(item.character);
        if (nestedId) return nestedId;
    }
    return (
        item._id ||
        item.id ||
        item.character_id ||
        item.characterId ||
        item.characterID ||
        null
    );
};

const normalizeForMatch = (value) => {
    if (!value) return '';
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
};

const findCharacterMatch = (list = [], names = [], { allowInverseMatch = true } = {}) => {
    if (!Array.isArray(list) || list.length === 0 || names.length === 0) return null;

    return list.find((item) => {
        const candidateNames = [
            item?.name,
            item?.character?.name,
            item?.display_name,
            item?.alias,
        ].filter(Boolean);

        const normalizedTargets = names.map(normalizeForMatch);

        return candidateNames.some((candidate) => {
            const normalizedCandidate = normalizeForMatch(candidate);
            return normalizedTargets.some((target) => {
                if (normalizedCandidate.includes(target)) return true;
                if (!allowInverseMatch) return false;
                return target.includes(normalizedCandidate);
            });
        });
    });
};

const DirectChat = ({ slug, darkMode, toggleDarkMode }) => {
    const [character, setCharacter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [authRequired, setAuthRequired] = useState(false);
    const navigate = useNavigate();

    const targetNames = EXPERT_SLUG_MAP[slug?.toLowerCase()] || [];

    // Store redirect path immediately on mount (before any async calls)
    useEffect(() => {
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath && currentPath !== '/') {
            localStorage.setItem('postLoginRedirect', currentPath);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            try {
                // Ensure we preserve intended redirect even if effects run late.
                const currentPath = window.location.pathname + window.location.search;
                if (currentPath && currentPath !== '/') {
                    localStorage.setItem('postLoginRedirect', currentPath);
                }

                const loginCheck = await apiCall.get('/users/check/login');
                const loggedIn = loginCheck?.data?.is_logged_in;

                if (!loggedIn) {
                    setAuthRequired(true);
                    return;
                }
                setAuthRequired(false);
                
                // User is logged in, clear the stored redirect since we're handling it now
                localStorage.removeItem('postLoginRedirect');

                const [companionsRes, subscriptionRes] = await Promise.all([
                    apiCall.get('/chat/companions'),
                    apiCall.get('/subscriptions/check').catch(() => null),
                ]);

                if (subscriptionRes) {
                    const subPayload = subscriptionRes?.data ?? subscriptionRes;
                    setHasSubscription(Boolean(subPayload?.has_subscription));
                }

                const payload = companionsRes?.data ?? companionsRes;
                const data = payload?.data ?? payload;

                const extractArray = (obj, keys) => {
                    if (!obj) return [];
                    for (const key of keys) {
                        if (Array.isArray(obj[key])) return obj[key];
                    }
                    if (Array.isArray(obj)) return obj;
                    return [];
                };

                const allowedCharacters = extractArray(data, [
                    'allowed_characters',
                    'allowedCharacters',
                    'characters',
                    'companions',
                    'character_list',
                ]);

                let match = null;
                const slugKey = slug?.toLowerCase();
                const allowInverseMatch = slugKey !== 'chinmaya';
                let fetchedCharacters = [];

                const isChinmayaCandidate = (candidate) => {
                    if (!candidate) return false;
                    const name =
                        candidate?.name ??
                        candidate?.character?.name ??
                        candidate?.display_name ??
                        candidate?.alias ??
                        '';
                    const normalized = normalizeForMatch(name);
                    return normalized.includes('chinmaya') || (normalized.includes('chin') && normalized.includes('maya'));
                };
                if (slug?.toLowerCase() === 'gujaratgovt') {
                    match = findCharacterById(allowedCharacters, GUJARAT_GOVT_CHARACTER_ID);
                }
                if (!match) {
                    match = findCharacterMatch(allowedCharacters, targetNames, { allowInverseMatch });
                    if (!match && slugKey === 'chinmaya') {
                        // Fallback: relaxed matching, but only if the candidate name truly contains "chin" and "maya".
                        const relaxed = findCharacterMatch(allowedCharacters, targetNames, { allowInverseMatch: true });
                        if (relaxed && isChinmayaCandidate(relaxed)) match = relaxed;
                    }
                }

                if (!match) {
                    const charsResponse = await apiCall.get('/character/profile/list');
                    const listPayload = charsResponse?.data ?? charsResponse;
                    fetchedCharacters = Array.isArray(listPayload) ? listPayload : listPayload?.data ?? [];
                    if (slug?.toLowerCase() === 'gujaratgovt') {
                        match = findCharacterById(fetchedCharacters, GUJARAT_GOVT_CHARACTER_ID);
                    }
                    if (!match) {
                        match = findCharacterMatch(fetchedCharacters, targetNames, { allowInverseMatch });
                        if (!match && slugKey === 'chinmaya') {
                            const relaxed = findCharacterMatch(fetchedCharacters, targetNames, { allowInverseMatch: true });
                            if (relaxed && isChinmayaCandidate(relaxed)) match = relaxed;
                        }
                    }
                }

                if (match) {
                    const normalized = match.character && typeof match.character === 'object'
                        ? { ...match.character, ...match }
                        : match;
                    const resolvedId = resolveCharacterId(normalized) || slug?.toLowerCase();
                    normalized._id = normalized._id || resolvedId;
                    normalized.id = normalized.id || resolvedId;
                    if (/scanneradda/i.test(normalized.name)) {
                        normalized.name = normalized.name.replace(/scanneradda/gi, 'Scanner');
                    }
                    normalized.intro_message = normalized.intro_message || INTRO_MESSAGES[slug?.toLowerCase()];
                    normalized.displayMessage = normalized.displayMessage || normalized.intro_message || 'Tap to start chatting...';
                    setCharacter(normalized);
                } else {
                    if (slugKey === 'chinmaya') {
                        const preview = (arr) =>
                            (Array.isArray(arr) ? arr : [])
                                .slice(0, 10)
                                .map((item) => item?.name ?? item?.character?.name ?? item?.display_name ?? item?.alias)
                                .filter(Boolean);
                        console.error('DirectChat chinmaya match not found', {
                            targetNames,
                            previewAllowedCharacters: preview(allowedCharacters),
                            previewFetchedCharacters: preview(fetchedCharacters),
                        });
                    }
                    navigate('/', { replace: true });
                }
            } catch (error) {
                console.error('DirectChat init error:', error);
                navigate('/', { replace: true });
            } finally {
                setLoading(false);
            }
        };

        if (targetNames.length > 0) {
            init();
        } else {
            navigate('/', { replace: true });
        }
    }, [slug, targetNames, navigate]);

    const handleBack = () => {
        navigate('/', { replace: true });
    };

    if (loading) {
        return (
            <div style={{ height: '100vh', backgroundColor: '#060712' }}>
                <Loading isLoading={true} />
            </div>
        );
    }

    if (authRequired) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#060712' }}>
                <LoginV3
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    onFirmLoginSuccess={() => {}}
                />
            </div>
        );
    }

    if (!character) {
        return null;
    }

    return (
        <Chat
            isUnidentifiedUser={false}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            selectedCharacter={character}
            onBackToCharacterSelection={handleBack}
            hasSubscription={hasSubscription}
        />
    );
};

export default DirectChat;
